import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSession } from '../utils/authUtils';

const TutorialContext = createContext(null);

// Tutorial step configuration
const TUTORIAL_STEPS = [
    {
        id: 1,
        target: 'view-profile',
        title: 'View Your Profile',
        message: 'Click here to view your business profile and manage your GMB listing.',
        page: '/dashboard',
        position: 'bottom',
        clickElement: true  // Clicking Next will click the View Profile button
    },
    {
        id: 2,
        target: 'reviews-section',
        title: 'Reviews Overview',
        message: 'Here you can see all your customer reviews and track your reputation.',
        page: '/business-profile',
        position: 'top',
        autoScroll: true,
        waitForElement: true  // Wait for element after page navigation
    },
    {
        id: 3,
        target: 'ai-review-reply',
        title: 'Activate AI Review Reply',
        message: 'Click here to enable AI-powered automatic replies to your customer reviews.',
        page: '/business-profile',
        position: 'right',
        clickElement: true  // Clicking Next will click the AI Reply button
    },
    {
        id: 4,
        target: 'ai-reply-time',
        title: 'Set Reply Timing',
        message: 'Choose when the AI should reply to reviews. Select your preferred timing and also choose which star ratings to auto-reply to below.',
        page: '/business-profile',
        position: 'bottom',
        waitForElement: true
    },
    {
        id: 5,
        target: 'set-ai-reply',
        title: 'Save Settings',
        message: 'Click this button to activate AI auto-reply for your reviews!',
        page: '/business-profile',
        position: 'top',
        waitForElement: true,
        clickElement: true  // Clicking Next will save the settings
    },
    // After AI Reply settings, navigate to Performance page
    {
        id: 6,
        target: 'performance-nav',
        title: 'Performance Analytics',
        message: 'Great! Now let\'s check your performance analytics. Click here to see your business metrics.',
        page: '/business-profile',
        position: 'right',
        waitForElement: true,  // Wait for AI Reply modal to close
        clickElement: true  // Clicking Next will click the Performance nav
    },
    // On Performance page, show Monthly Plan nav
    {
        id: 7,
        target: 'monthly-plan-nav',
        title: 'Monthly Plan',
        message: 'Explore Performance analytics on your own! Now click here to see your monthly marketing plan.',
        page: '/performance',
        position: 'right',
        waitForElement: true,
        clickElement: true  // Clicking Next will navigate to Monthly Plan
    },
    // On Monthly Plan page, show Auto Generate button (profile auto-selected or dialog handled separately)
    {
        id: 8,
        target: 'auto-generate-month',
        title: 'Auto Generate Plan',
        message: 'Click "Auto Generate Month" to let AI create a complete marketing plan for you! You can explore this feature later.',
        page: '/monthly-plan',
        position: 'bottom',
        waitForElement: true
        // No clickElement - just show, don't click
    },
    // Navigate to Reputation Management
    {
        id: 9,
        target: 'reputation-nav',
        title: 'Reputation Management',
        message: 'Now let\'s explore Reputation Management. Click here to manage your reviews and create posts!',
        page: '/monthly-plan',
        position: 'right',
        waitForElement: true,
        clickElement: true  // Clicking Next will navigate to Reputation
    },
    // Select profile on Reputation page
    {
        id: 10,
        target: 'reputation-profile',
        title: 'Select Profile',
        message: 'Select your business profile to view reviews.',
        page: '/reputation',
        position: 'bottom',
        waitForElement: true,
        clickElement: true  // Clicking Next will select the first profile
    },
    // Scroll to Reviews Overview section
    {
        id: 11,
        target: 'reviews-overview',
        title: 'Reviews Overview',
        message: 'Here you can see all your customer reviews and their ratings.',
        page: '/reputation',
        position: 'bottom',
        autoScroll: true,
        scrollDelay: 500,
        waitForElement: true
    },
    // Click on AI Reply button
    {
        id: 12,
        target: 'reputation-ai-reply',
        title: 'AI Reply Feature',
        message: 'This button lets you set up AI to automatically reply to your reviews! Explore this feature later.',
        page: '/reputation',
        position: 'right',
        waitForElement: true
        // No clickElement - just show, don't navigate away
    },
    // Show Create Post button
    {
        id: 13,
        target: 'create-testimonial-post',
        title: 'Create Post',
        message: 'Click "Create Post" to generate a beautiful testimonial post. You can post this directly to your GMB profile!',
        page: '/reputation',
        position: 'top',
        waitForElement: true
        // No clickElement - just show, let user explore
    },
    // Then navigate to Free Website
    {
        id: 14,
        target: 'free-website-nav',
        title: 'Create Free Website',
        message: 'Great! Now let\'s create a beautiful free website for your business. Click here!',
        page: '/reputation',
        position: 'right',
        waitForElement: true,
        clickElement: true  // Clicking Next will navigate to Free Website
    },
    {
        id: 15,
        target: 'template-card',
        title: 'Choose a Template',
        message: 'Click on a template that matches your business style.',
        page: '/free-website',
        position: 'bottom',
        clickElement: true,  // Clicking Next will click the template card
        waitForElement: true  // Wait for element after page navigation
    },
    {
        id: 16,
        target: 'use-template',
        title: 'Use This Template',
        message: 'Click "Use Template" to select this design for your website.',
        page: '/free-website',
        position: 'top',
        waitForElement: true,  // Wait for template preview to appear
        clickElement: true  // Clicking Next will click Use Template
    },
    {
        id: 17,
        target: 'skip-services',
        title: 'Skip Services Setup',
        message: 'You can add services and products later. Click "Skip for Now" to continue with website creation.',
        page: '/free-website',
        position: 'top',
        waitForElement: true,  // Wait for the popup to appear
        clickElement: true  // Clicking Next will click Skip for Now
    },
    {
        id: 18,
        target: 'deploy-directory',
        title: 'Deploy to Directory',
        message: 'Your website is ready! Click "Deploy to Directory" to publish it to your GMB profile. Congratulations on completing the tutorial!',
        page: '/free-website',
        position: 'top',
        waitForElement: true,  // Wait for website creation to complete
        isFinal: true
    }
];

const TUTORIAL_STORAGE_KEY = 'visibeen.tutorialProgress:';
const TUTORIAL_COMPLETE_KEY = 'visibeen.tutorialComplete:';

export const TutorialProvider = ({ children }) => {
    const [isActive, setIsActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [targetElement, setTargetElement] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Get user ID for storage - prioritize email for consistent identification across sessions
    const getUserId = useCallback(() => {
        const session = getSession();
        // Use email first as it's consistent across login/logout cycles
        return session?.email || session?.id || session?.user_id || session?.userId || 'default';
    }, []);

    // Check if tutorial is completed
    const isTutorialComplete = useCallback(() => {
        const userId = getUserId();
        return localStorage.getItem(`${TUTORIAL_COMPLETE_KEY}${userId}`) === 'true';
    }, [getUserId]);

    // Mark tutorial as complete
    const markTutorialComplete = useCallback(() => {
        const userId = getUserId();
        localStorage.setItem(`${TUTORIAL_COMPLETE_KEY}${userId}`, 'true');
        localStorage.removeItem(`${TUTORIAL_STORAGE_KEY}${userId}`);
    }, [getUserId]);

    // Save progress
    const saveProgress = useCallback((step) => {
        const userId = getUserId();
        localStorage.setItem(`${TUTORIAL_STORAGE_KEY}${userId}`, step.toString());
    }, [getUserId]);

    // Load progress
    const loadProgress = useCallback(() => {
        const userId = getUserId();
        const saved = localStorage.getItem(`${TUTORIAL_STORAGE_KEY}${userId}`);
        return saved ? parseInt(saved, 10) : 0;
    }, [getUserId]);

    // Find and highlight target element
    const findAndHighlightTarget = useCallback(() => {
        if (!isActive || currentStep >= TUTORIAL_STEPS.length) {
            setTargetElement(null);
            setIsVisible(false);
            return;
        }

        const step = TUTORIAL_STEPS[currentStep];
        console.log('[Tutorial] Looking for element:', step.target, 'Step:', currentStep);
        const element = document.querySelector(`[data-tutorial="${step.target}"]`);
        console.log('[Tutorial] Found element:', element);

        if (element) {
            setTargetElement(element);
            setIsVisible(true);
            console.log('[Tutorial] Element visible, setting targetElement');

            // Auto-scroll if needed
            if (step.autoScroll) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, step.scrollDelay || 300);
            }
        } else if (step.waitForElement) {
            // Keep looking for the element
            console.log('[Tutorial] Element not found, retrying in 500ms...');
            setIsVisible(false);
            setTimeout(findAndHighlightTarget, 500);
        } else {
            console.log('[Tutorial] Element not found and no waitForElement flag');
            setTargetElement(null);
            setIsVisible(false);
        }
    }, [isActive, currentStep]);

    // Start tutorial
    const startTutorial = useCallback((fromStep = 0) => {
        if (isTutorialComplete()) return;

        setCurrentStep(fromStep);
        setIsActive(true);
        saveProgress(fromStep);

        // Navigate to the first step's page if needed
        const step = TUTORIAL_STEPS[fromStep];
        if (step && !location.pathname.includes(step.page.split('?')[0])) {
            navigate(step.page);
        }
    }, [isTutorialComplete, location.pathname, navigate, saveProgress]);

    // Resume tutorial
    const resumeTutorial = useCallback(() => {
        if (isTutorialComplete()) return;

        const savedStep = loadProgress();
        startTutorial(savedStep);
    }, [isTutorialComplete, loadProgress, startTutorial]);

    // Next step
    const nextStep = useCallback(() => {
        const currentStepConfig = TUTORIAL_STEPS[currentStep];

        // If current step has clickElement flag, click the target element
        if (currentStepConfig?.clickElement && targetElement) {
            // Click the highlighted element to trigger its action (navigation, open modal, etc.)
            targetElement.click();
        }

        const nextStepIndex = currentStep + 1;

        if (nextStepIndex >= TUTORIAL_STEPS.length) {
            // Tutorial complete
            setIsActive(false);
            setIsVisible(false);
            setTargetElement(null);
            markTutorialComplete();

            // Show completion message
            alert('🎉 Congratulations! You have completed the tutorial. Welcome to Visibeen!');

            // Redirect to dashboard
            navigate('/dashboard');
            return;
        }

        const nextStepConfig = TUTORIAL_STEPS[nextStepIndex];
        setCurrentStep(nextStepIndex);
        saveProgress(nextStepIndex);

        // Only navigate if clickElement is NOT set (otherwise the element click handles navigation)
        if (nextStepConfig && !currentStepConfig?.clickElement && !location.pathname.includes(nextStepConfig.page.split('?')[0])) {
            navigate(nextStepConfig.page);
        }
    }, [currentStep, targetElement, location.pathname, markTutorialComplete, navigate, saveProgress]);

    // Previous step
    const prevStep = useCallback(() => {
        if (currentStep > 0) {
            const prevStepIndex = currentStep - 1;
            const prevStepConfig = TUTORIAL_STEPS[prevStepIndex];
            setCurrentStep(prevStepIndex);
            saveProgress(prevStepIndex);

            if (prevStepConfig && !location.pathname.includes(prevStepConfig.page.split('?')[0])) {
                navigate(prevStepConfig.page);
            }
        }
    }, [currentStep, location.pathname, navigate, saveProgress]);

    // Skip tutorial
    const skipTutorial = useCallback(() => {
        setIsActive(false);
        setIsVisible(false);
        setTargetElement(null);
        markTutorialComplete();
    }, [markTutorialComplete]);

    // Handle element clicks during tutorial
    const handleTutorialClick = useCallback(() => {
        if (isActive && isVisible) {
            nextStep();
        }
    }, [isActive, isVisible, nextStep]);

    // Watch for DOM changes and re-find target
    useEffect(() => {
        if (isActive) {
            findAndHighlightTarget();
        }
    }, [isActive, currentStep, location.pathname, findAndHighlightTarget]);

    // Periodic check for dynamic elements
    useEffect(() => {
        if (!isActive) return;

        const interval = setInterval(() => {
            if (!isVisible && isActive) {
                findAndHighlightTarget();
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isActive, isVisible, findAndHighlightTarget]);

    const currentStepConfig = TUTORIAL_STEPS[currentStep] || null;

    const value = {
        isActive,
        currentStep,
        currentStepConfig,
        totalSteps: TUTORIAL_STEPS.length,
        isVisible,
        targetElement,
        startTutorial,
        resumeTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        handleTutorialClick,
        isTutorialComplete,
        TUTORIAL_STEPS
    };

    return (
        <TutorialContext.Provider value={value}>
            {children}
        </TutorialContext.Provider>
    );
};

export const useTutorial = () => {
    const context = useContext(TutorialContext);
    if (!context) {
        throw new Error('useTutorial must be used within a TutorialProvider');
    }
    return context;
};

export default TutorialContext;
